// Question + ending bodies for the Surveys 2.0 renderer. Markup and class
// names mirror the approved mock (design-handoffs/surveys-v2/mockup.html):
// .sv-from / .sv-qn / .sv-title / .sv-desc / .sv-ctl / .sv-next, prefixed
// `sv2-` here so nothing collides with the legacy form styles.
import {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { pickLocalized, TokenContext, fillTokens } from "../../Helper/surveyText";
import type {
  SurveyAnswerValue,
  SurveyBlock,
  SurveyDefinition,
  SurveyEnding,
  SurveyWelcome,
} from "../../Helper/surveyTypes";
import { useTicketStore } from "../../Stores/Ticket";
import { useConfigStore } from "../../Stores/Config";
import Communicator from "../../Helper/Communicator";
import { SurveyErrorCode, useSurveyV2Store } from "../../Stores/SurveyV2";
import { SurveyIcon } from "./SurveyIcons";
import type { SurveyStrings } from "./surveyStrings";

export type SurveyMode = "card" | "full" | "page";

export interface SurveyRenderContext {
  mode: SurveyMode;
  sheet: boolean;
  def: SurveyDefinition;
  lang: string;
  t: SurveyStrings;
  tokens: TokenContext;
  hasKeyboard: boolean;
  sender?: { name?: string; avatar?: string; company?: string };
}

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const EMOJI_5 = ["😡", "😢", "😐", "😊", "🤩"];
// Legacy NPS emoji values (FeedbackFlow NPSFormItem) so migrated surveys keep
// their scale semantics.
const NPS_EMOJI_VALUES = [0, 2, 6, 8, 10];

const AUTO_ADVANCE_MS = 320;
const AUTO_ADVANCE_TYPES = ["single", "yesno", "nps", "csat", "rating"];

export const isAutoAdvance = (block: SurveyBlock) =>
  AUTO_ADVANCE_TYPES.includes(block.type);

export const text = (
  ctx: SurveyRenderContext,
  value: Record<string, string> | string | undefined,
) => fillTokens(pickLocalized(value as any, ctx.lang, ctx.def.defaultLanguage), ctx.tokens);

/** Roving focus inside a radiogroup / group: arrows move between options. */
const rovingKeys = (event: ReactKeyboardEvent<HTMLElement>) => {
  const keys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
  if (!keys.includes(event.key)) return;
  const items = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>("[data-roving]"),
  ).filter((el) => !el.hasAttribute("disabled"));
  const index = items.indexOf(document.activeElement as HTMLElement);
  if (index < 0) return;
  event.preventDefault();
  event.stopPropagation();
  const rtl = getComputedStyle(event.currentTarget).direction === "rtl";
  let next = index;
  if (event.key === "Home") next = 0;
  else if (event.key === "End") next = items.length - 1;
  else if (
    event.key === "ArrowDown" ||
    (event.key === "ArrowRight" && !rtl) ||
    (event.key === "ArrowLeft" && rtl)
  )
    next = Math.min(items.length - 1, index + 1);
  else next = Math.max(0, index - 1);
  items[next]?.focus();
};

const rovingIndex = (selectedIndex: number, index: number) =>
  (selectedIndex >= 0 ? selectedIndex : 0) === index ? 0 : -1;

// ── Scales ────────────────────────────────────────────────────────────────

interface ScaleOption {
  value: number;
  label: string;
  aria: string;
  /** The key that picks it (full screen digits); the value unless the label differs. */
  digit?: number;
}

const scaleOptions = (block: SurveyBlock, t: SurveyStrings) => {
  let style = block.scaleStyle;
  let min = 1;
  let max = 5;
  if (block.type === "nps" && style === "compact") {
    // The legacy "modern" NPS: 0–5 on screen, 0, 2, …, 10 stored, so NPS groups are unchanged.
    const options = [0, 1, 2, 3, 4, 5].map((n) => ({ value: n * 2, label: String(n), aria: String(n), digit: n }));
    return { options, style: "numbers" as const };
  }
  if (block.type === "nps") {
    min = 0;
    max = 10;
    style = style === "emoji" ? "emoji" : "numbers";
  } else if (block.type === "csat") {
    style = style ?? "emoji";
  } else if (block.type === "rating") {
    min = block.scaleMin ?? 1;
    max = block.scaleMax ?? 5;
    style = style ?? "stars";
  } else {
    min = block.scaleMin ?? 1;
    max = block.scaleMax ?? 5;
    style = style ?? "numbers";
  }
  if (max < min) [min, max] = [max, min];
  if (max - min > 10) max = min + 10;

  let options: ScaleOption[] = [];
  if (style === "emoji") {
    const values =
      block.type === "nps"
        ? NPS_EMOJI_VALUES
        : max - min === 4
        ? [min, min + 1, min + 2, min + 3, max]
        : [0, 1, 2, 3, 4].map((i) => Math.round(min + ((max - min) * i) / 4));
    options = values.map((value, i) => ({
      value,
      label: EMOJI_5[i],
      aria: t("svStarsOf", { n: i + 1, max: 5 }),
    }));
  } else {
    for (let v = min; v <= max; v++) {
      options.push({
        value: v,
        label: String(v),
        aria:
          style === "stars"
            ? t("svStarsOf", { n: v - min + 1, max: max - min + 1 })
            : String(v),
      });
    }
  }
  return { options, style: style as "numbers" | "emoji" | "stars" };
};

function ScaleControl({
  block,
  value,
  ctx,
  titleId,
  onPick,
}: {
  block: SurveyBlock;
  value: SurveyAnswerValue | undefined;
  ctx: SurveyRenderContext;
  titleId: string;
  onPick: (value: number) => void;
}) {
  const { options, style } = scaleOptions(block, ctx.t);
  const [hover, setHover] = useState<number | null>(null);
  const selectedIndex = options.findIndex((o) => o.value === value);
  let low = text(ctx, block.lowLabel);
  let high = text(ctx, block.highLabel);
  if (block.type === "nps" && !low && !high) {
    low = ctx.t("svNotLikely");
    high = ctx.t("svVeryLikely");
  }

  const hoverIndex = hover !== null ? options.findIndex((o) => o.value === hover) : -1;
  const fillUpTo = hoverIndex >= 0 ? hoverIndex : selectedIndex;

  return (
    <div className="sv2-ctl">
      <div
        className={`sv2-scale sv2-scale--${style} ${
          // More than 7 numbers wrap into two even rows on narrow screens.
          style === "numbers" && options.length > 7 ? "sv2-scale--wide" : ""
        }`}
        role="radiogroup"
        aria-labelledby={titleId}
        // The end labels ("Not likely" … "Very likely") give the numbers their meaning.
        aria-describedby={low || high ? `${titleId}-ends` : undefined}
        aria-required={block.required}
        style={
          style === "numbers"
            ? ({
                gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
                "--sv2-per-row": Math.ceil(options.length / 2),
              } as any)
            : undefined
        }
        onKeyDown={rovingKeys}
        onPointerLeave={() => setHover(null)}
      >
        {options.map((option, index) => {
          const selected = index === selectedIndex;
          const filled = style === "stars" && index <= fillUpTo;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={style === "numbers" ? undefined : option.aria}
              tabIndex={rovingIndex(selectedIndex, index)}
              data-roving
              data-digit={option.digit ?? option.value}
              className={`sv2-scale-item ${selected ? "is-selected" : ""} ${
                filled ? "is-filled" : ""
              }`}
              // Hover preview for a real mouse only: a tap's compat mouse
              // events would leave stars filled that aren't picked.
              onPointerEnter={(event) =>
                style === "stars" &&
                event.pointerType === "mouse" &&
                setHover(option.value)
              }
              onClick={() => onPick(option.value)}
            >
              {style === "stars" ? (
                <SurveyIcon name="star" size={ctx.mode === "card" ? 26 : 34} strokeWidth={1.6} filled={filled} />
              ) : (
                <span className={style === "emoji" ? "sv2-emoji" : undefined}>
                  {option.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {(low || high) && (
        <div className="sv2-scale-labels" id={`${titleId}-ends`}>
          <span>{low}</span>
          {low && high && <span className="sv2-sr"> – </span>}
          <span>{high}</span>
        </div>
      )}
    </div>
  );
}

// ── Choices ───────────────────────────────────────────────────────────────

const OTHER_ID = "__other";

function ChoiceControl({
  block,
  value,
  ctx,
  titleId,
  order,
  onChange,
  onAutoAdvance,
}: {
  block: SurveyBlock;
  value: SurveyAnswerValue | undefined;
  ctx: SurveyRenderContext;
  titleId: string;
  order?: string[];
  onChange: (value: SurveyAnswerValue | undefined) => void;
  onAutoAdvance: () => void;
}) {
  const multi = block.type === "multi";
  const otherInputRef = useRef<HTMLInputElement>(null);
  const choices = (block.choices ?? []).slice();
  if (order) {
    choices.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  }

  // Normalise the draft into { picked ids, other text | undefined }.
  let picked: string[] = [];
  let otherText: string | undefined;
  if (Array.isArray(value)) picked = value as string[];
  else if (typeof value === "string") picked = [value];
  else if (value && typeof value === "object") {
    picked = ((value as any).choices ?? []) as string[];
    otherText = (value as any).other ?? "";
  }
  const otherOn = otherText !== undefined;

  const build = (ids: string[], other: string | undefined): SurveyAnswerValue | undefined => {
    if (multi) {
      if (other !== undefined) return { choices: ids, other };
      return ids.length ? ids : undefined;
    }
    if (other !== undefined) return { other };
    return ids[0];
  };

  const count = picked.length + (otherOn ? 1 : 0);
  const maxReached = multi && !!block.maxSelect && count >= block.maxSelect;

  const toggle = (id: string) => {
    if (id === OTHER_ID) {
      if (multi) {
        if (!otherOn && maxReached) return;
        onChange(build(picked, otherOn ? undefined : otherText ?? ""));
      } else {
        onChange(otherOn ? undefined : { other: "" });
      }
      if (!otherOn) setTimeout(() => otherInputRef.current?.focus(), 30);
      return;
    }
    if (multi) {
      const has = picked.includes(id);
      if (!has && maxReached) return;
      onChange(build(has ? picked.filter((p) => p !== id) : [...picked, id], otherText));
      return;
    }
    onChange(id);
    onAutoAdvance();
  };

  const items: { id: string; label: string }[] = choices.map((c) => ({
    id: c.id,
    label: text(ctx, c.label),
  }));
  if (block.allowOther) items.push({ id: OTHER_ID, label: ctx.t("svOther") });
  const selectedIndex = items.findIndex((item) =>
    item.id === OTHER_ID ? otherOn : picked.includes(item.id),
  );

  return (
    <div className="sv2-ctl">
      <div
        className="sv2-opts"
        role={multi ? "group" : "radiogroup"}
        aria-labelledby={titleId}
        aria-required={block.required}
        onKeyDown={rovingKeys}
      >
        {items.map((item, index) => {
          const selected = item.id === OTHER_ID ? otherOn : picked.includes(item.id);
          const disabled = !selected && maxReached;
          return (
            <button
              key={item.id}
              type="button"
              role={multi ? "checkbox" : "radio"}
              aria-checked={selected}
              aria-disabled={disabled || undefined}
              tabIndex={rovingIndex(selectedIndex, index)}
              data-roving
              data-key={LETTERS[index]}
              className={`sv2-opt ${selected ? "is-selected" : ""} ${
                disabled ? "is-disabled" : ""
              }`}
              onClick={() => toggle(item.id)}
            >
              <span className="sv2-key" aria-hidden="true">
                {LETTERS[index]}
              </span>
              <span className="sv2-opt-label">{item.label}</span>
              <SurveyIcon name="check" size={16} className="sv2-chk" />
            </button>
          );
        })}
      </div>
      {otherOn && (
        <input
          ref={otherInputRef}
          className="sv2-input sv2-other-input"
          type="text"
          value={otherText ?? ""}
          maxLength={500}
          placeholder={ctx.t("svOtherPlaceholder")}
          aria-label={ctx.t("svOther")}
          onChange={(e) => onChange(build(picked, e.target.value))}
        />
      )}
    </div>
  );
}

function YesNoControl({
  block,
  value,
  ctx,
  titleId,
  onPick,
}: {
  block: SurveyBlock;
  value: SurveyAnswerValue | undefined;
  ctx: SurveyRenderContext;
  titleId: string;
  onPick: (value: boolean) => void;
}) {
  const yes = ctx.t("svYes");
  const no = ctx.t("svNo");
  let keys = [yes.charAt(0).toUpperCase(), no.charAt(0).toUpperCase()];
  if (!keys[0] || keys[0] === keys[1]) keys = ["A", "B"];
  const items = [
    { v: true, label: yes, key: keys[0] },
    { v: false, label: no, key: keys[1] },
  ];
  const selectedIndex = value === true ? 0 : value === false ? 1 : -1;
  return (
    <div className="sv2-ctl">
      <div
        className="sv2-opts sv2-yn"
        role="radiogroup"
        aria-labelledby={titleId}
        aria-required={block.required}
        onKeyDown={rovingKeys}
      >
        {items.map((item, index) => (
          <button
            key={item.key}
            type="button"
            role="radio"
            aria-checked={selectedIndex === index}
            tabIndex={rovingIndex(selectedIndex, index)}
            data-roving
            data-key={item.key}
            className={`sv2-opt ${selectedIndex === index ? "is-selected" : ""}`}
            onClick={() => onPick(item.v)}
          >
            <span className="sv2-key" aria-hidden="true">
              {item.key}
            </span>
            <span className="sv2-opt-label">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Inputs ────────────────────────────────────────────────────────────────

function TextControl({
  block,
  value,
  ctx,
  titleId,
  errorId,
  invalid,
  onChange,
}: {
  block: SurveyBlock;
  value: SurveyAnswerValue | undefined;
  ctx: SurveyRenderContext;
  titleId: string;
  errorId: string;
  invalid: boolean;
  onChange: (value: string) => void;
}) {
  const placeholderText = text(ctx, block.placeholder);
  const common = {
    className: `sv2-input ${block.type === "long" ? "sv2-ta" : ""}`,
    "aria-labelledby": titleId,
    "aria-describedby": invalid ? errorId : undefined,
    "aria-invalid": invalid || undefined,
    "aria-required": block.required,
    "data-autofocus": true,
    value: value === undefined || value === null ? "" : String(value),
    maxLength: block.maxLength && block.maxLength > 0 ? block.maxLength : undefined,
  };

  if (block.type === "long") {
    const length = String(value ?? "").length;
    const max = block.maxLength ?? 0;
    return (
      <div className="sv2-ctl">
        <textarea
          {...common}
          rows={ctx.mode === "card" ? 3 : 2}
          placeholder={placeholderText || ctx.t("svTypeAnswer")}
          onChange={(e) => onChange(e.target.value)}
        />
        {max > 0 && length > max * 0.8 && (
          <div className="sv2-counter" aria-live="polite">
            {ctx.t("svCharsLeft", { n: Math.max(0, max - length) })}
          </div>
        )}
      </div>
    );
  }

  const typeProps: Record<string, any> =
    block.type === "email"
      ? { type: "email", inputMode: "email", autoComplete: "email", placeholder: placeholderText || ctx.t("svEmailPlaceholder") }
      : block.type === "number"
      ? { type: "text", inputMode: "decimal", placeholder: placeholderText || ctx.t("svNumberPlaceholder") }
      : block.type === "date"
      ? { type: "date" }
      : { type: "text", placeholder: placeholderText || ctx.t("svTypeAnswer") };

  return (
    <div className={`sv2-ctl ${block.type === "date" ? "sv2-ctl--date" : ""}`}>
      <input {...common} {...typeProps} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/** Whether a file matches an <input accept> list (".png", "image/*", "application/pdf"); no list = any file. */
export const fileMatchesAccept = (file: { name: string; type?: string }, accept?: string) => {
  const rules = (accept ?? "").split(",").map((rule) => rule.trim().toLowerCase()).filter(Boolean);
  if (rules.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = (file.type ?? "").toLowerCase();
  return rules.some((rule) =>
    rule.startsWith(".")
      ? name.endsWith(rule)
      : rule.endsWith("/*")
      ? type.startsWith(rule.slice(0, -1))
      : type === rule,
  );
};

// ── Consent ───────────────────────────────────────────────────────────────

function ConsentControl({
  block,
  value,
  ctx,
  titleId,
  onChange,
}: {
  block: SurveyBlock;
  value: SurveyAnswerValue | undefined;
  ctx: SurveyRenderContext;
  titleId: string;
  onChange: (value: true | undefined) => void;
}) {
  const translate = useConfigStore((state) => state.translateTextWithFallback);
  const custom = text(ctx, block.title);
  const linkLabel = text(ctx, block.linkLabel) || translate("ppolicy", "privacy policy");
  const openLink = (event: ReactMouseEvent | ReactKeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (block.linkUrl) Communicator.openURL(block.linkUrl);
  };
  const link = block.linkUrl ? (
    <a href={block.linkUrl} className="sv2-consent-link" target="_blank" rel="noopener noreferrer" onClick={openLink}>
      {linkLabel}
    </a>
  ) : null;
  return (
    <div className="sv2-ctl">
      <label className="sv2-consent">
        <input
          type="checkbox"
          checked={value === true}
          aria-labelledby={`${titleId}-consent`}
          aria-required={block.required}
          data-autofocus
          onChange={(e) => onChange(e.currentTarget.checked ? true : undefined)}
        />
        <span id={`${titleId}-consent`}>
          {custom ? (
            <>
              {custom}
              {link && <> {link}</>}
            </>
          ) : (
            <>
              {translate("ppolicyIRead", "I have read and accept the ")}
              {link ?? linkLabel}.
            </>
          )}
        </span>
      </label>
    </div>
  );
}

function UploadControl({
  block,
  value,
  ctx,
  titleId,
  onChange,
}: {
  block: SurveyBlock;
  value: SurveyAnswerValue | undefined;
  ctx: SurveyRenderContext;
  titleId: string;
  onChange: (value: string[] | undefined) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [dragging, setDragging] = useState(false);
  const [names, setNames] = useState<Record<string, string>>({});
  const files = Array.isArray(value) ? (value as string[]) : [];

  const upload = (file?: File | null) => {
    if (!file) return;
    // Dropped files skip the picker's accept filter.
    if (!fileMatchesAccept(file, block.accept)) {
      setError(ctx.t("svErrFileType"));
      return;
    }
    setError(undefined);
    setProgress(0);
    useTicketStore
      .getState()
      .uploadFile(file, (pct) => setProgress(pct))
      .then((url) => {
        setProgress(null);
        setNames((n) => ({ ...n, [url]: file.name }));
        onChange([...files, url]);
      })
      .catch((err) => {
        setProgress(null);
        setError(err?.message || ctx.t("svUploadFailed"));
      });
  };

  const fileName = (url: string) =>
    names[url] ?? decodeURIComponent(url.split("?")[0].split("/").pop() ?? url);

  return (
    <div className="sv2-ctl">
      {files.map((url) => (
        <div className="sv2-file" key={url}>
          <SurveyIcon name="paperclip" size={15} />
          <span className="sv2-file-name">{fileName(url)}</span>
          <button
            type="button"
            className="sv2-file-remove"
            aria-label={ctx.t("svRemoveFile")}
            onClick={() => onChange(files.filter((f) => f !== url))}
          >
            <SurveyIcon name="x" size={14} />
          </button>
        </div>
      ))}
      {progress !== null ? (
        <div className="sv2-drop sv2-drop--busy" aria-live="polite">
          <span>{ctx.t("svUploading")}</span>
          <div className="sv2-drop-progress">
            <i style={{ width: `${Math.max(6, progress)}%` }} />
          </div>
        </div>
      ) : files.length === 0 ? (
        <button
          type="button"
          className={`sv2-drop ${dragging ? "is-dragging" : ""}`}
          aria-labelledby={titleId}
          data-autofocus
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            upload(e.dataTransfer?.files?.[0]);
          }}
        >
          <SurveyIcon name="upload" size={18} />
          <span>
            <b>{ctx.t("svChooseFile")}</b> {ctx.t("svDropFile")}
          </span>
        </button>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        hidden
        tabIndex={-1}
        accept={block.accept || undefined}
        onChange={(e) => {
          upload(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      {error && (
        <p className="sv2-error" role="alert">
          <SurveyIcon name="alert" size={14} />
          {error}
        </p>
      )}
    </div>
  );
}

// ── Next row ──────────────────────────────────────────────────────────────

const hasDraftValue = (value: SurveyAnswerValue | undefined) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    const draft = value as { choices?: string[]; other?: string };
    return (draft.choices?.length ?? 0) > 0 || draft.other !== undefined;
  }
  return true;
};

const leadsOnlyToEndings = (def: SurveyDefinition, block: SurveyBlock) => {
  const endingIds = new Set(def.endings.map((e) => e.id));
  const index = def.blocks.findIndex((b) => b.id === block.id);
  const fallback = block.next ?? def.blocks[index + 1]?.id ?? def.endings[0]?.id;
  const targets = [...(block.jumps ?? []).map((j) => j.go), fallback];
  return targets.every((id) => !id || endingIds.has(id));
};

function NextRow({
  block,
  ctx,
  showButton,
  answered,
  onNext,
  onSkip,
}: {
  block: SurveyBlock;
  ctx: SurveyRenderContext;
  showButton: boolean;
  /** Something is typed / picked: Skip would throw it away, so only Next shows. */
  answered: boolean;
  onNext: () => void;
  onSkip: () => void;
}) {
  const full = ctx.mode !== "card";
  const last = leadsOnlyToEndings(ctx.def, block);
  const label =
    block.type === "statement"
      ? ctx.t("svContinue")
      : last
      ? ctx.t("svSubmit")
      : full
      ? ctx.t("svOk")
      : ctx.t("svNext");
  const skip = !block.required && block.type !== "statement" && !answered ? (
    <button type="button" className="sv2-skip" onClick={onSkip}>
      {ctx.t("svSkip")}
    </button>
  ) : null;

  if (!showButton && !skip) return null;

  return (
    <div className={`sv2-next ${showButton ? "" : "sv2-next--skip-only"}`}>
      {!full && (skip ?? <span />)}
      {showButton && (
        <span className="sv2-next-main">
          <button type="button" className="sv2-btn" onClick={onNext}>
            {label}
            {full && !last && block.type !== "statement" && (
              <SurveyIcon name="check" size={16} strokeWidth={2.4} />
            )}
          </button>
          {full && ctx.hasKeyboard && (
            <span className="sv2-hint" aria-hidden="true">
              {ctx.t("svPressEnter")} <b>{ctx.t("svEnter")} ↵</b>
            </span>
          )}
        </span>
      )}
      {full && skip}
    </div>
  );
}

// ── Question ──────────────────────────────────────────────────────────────

const errorText = (
  ctx: SurveyRenderContext,
  block: SurveyBlock,
  code: SurveyErrorCode,
) => {
  switch (code) {
    case "email":
      return ctx.t("svErrEmail");
    case "number":
      return ctx.t("svErrNumber");
    case "date":
      return ctx.t("svErrDate");
    case "minSelect":
      return ctx.t("svErrMinSelect", { n: block.minSelect ?? 1 });
    case "maxSelect":
      return ctx.t("svErrMaxSelect", { n: block.maxSelect ?? 1 });
    case "other":
      return ctx.t("svErrOther");
    case "consent":
      return ctx.t("svErrConsent");
    default:
      return ctx.t("svErrRequired");
  }
};

const selectionHint = (ctx: SurveyRenderContext, block: SurveyBlock) => {
  if (block.type !== "multi") return null;
  const { minSelect: min, maxSelect: max } = block;
  if (min && max && min > 1) return ctx.t("svChooseBetween", { min, max });
  if (max) return ctx.t("svChooseUpTo", { n: max });
  if (min && min > 1) return ctx.t("svChooseAtLeast", { n: min });
  return null;
};

function Media({ media }: { media: SurveyBlock["media"] }) {
  if (!media?.url) return null;
  if (media.type === "video") {
    const yt = media.url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/,
    );
    const vimeo = media.url.match(/vimeo\.com\/(\d+)/);
    const embed = yt
      ? `https://www.youtube-nocookie.com/embed/${yt[1]}`
      : vimeo
      ? `https://player.vimeo.com/video/${vimeo[1]}`
      : null;
    return (
      <div className="sv2-media sv2-media--video">
        {embed ? (
          <iframe src={embed} title="Video" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
        ) : (
          <video src={media.url} controls playsInline preload="metadata" />
        )}
      </div>
    );
  }
  return (
    <div className="sv2-media">
      <img src={media.url} alt="" loading="lazy" />
    </div>
  );
}

export function SenderRow({ ctx }: { ctx: SurveyRenderContext }) {
  const sender = ctx.sender;
  if (!sender?.name) return null;
  return (
    <div className="sv2-from">
      {sender.avatar ? (
        <img className="sv2-ava" src={sender.avatar} alt="" />
      ) : (
        <span className="sv2-ava" aria-hidden="true">
          {sender.name.charAt(0).toUpperCase()}
        </span>
      )}
      <span>
        <b>{sender.name}</b>
        {sender.company ? ` ${ctx.t("svFrom")} ${sender.company}` : ""}
      </span>
    </div>
  );
}

export function QuestionBody({
  block,
  ctx,
  active,
}: {
  block: SurveyBlock;
  ctx: SurveyRenderContext;
  active: boolean;
}) {
  const draft = useSurveyV2Store((s) => s.drafts[block.id]);
  const error = useSurveyV2Store((s) => s.errors[block.id]);
  const order = useSurveyV2Store((s) => s.choiceOrder[block.id]);
  const number = useSurveyV2Store((s) => s.questionNumber(block.id));
  const setDraft = useSurveyV2Store((s) => s.setDraft);
  const submitCurrent = useSurveyV2Store((s) => s.submitCurrent);
  const skipCurrent = useSurveyV2Store((s) => s.skipCurrent);
  const autoTimer = useRef<any>(null);
  const shakeRef = useRef<HTMLDivElement>(null);
  const [shake, setShake] = useState(0);

  // Restart the shake animation without remounting the control (a remount
  // would drop the focus and caret of a text input).
  useEffect(() => {
    const el = shakeRef.current;
    if (!shake || !el) return;
    el.classList.remove("sv2-shake");
    void el.offsetWidth;
    el.classList.add("sv2-shake");
  }, [shake]);

  useEffect(() => () => clearTimeout(autoTimer.current), []);

  // A preselected answer fills an untouched question (never one answered or cleared before).
  useEffect(() => {
    if (!active || block.defaultValue === undefined) return;
    const state = useSurveyV2Store.getState();
    if (block.id in state.drafts || state.answers[block.key] !== undefined) return;
    setDraft(block.id, block.defaultValue as SurveyAnswerValue);
  }, [active, block.id]);

  const titleId = `sv2-q-${block.id}`;
  const errorId = `sv2-e-${block.id}`;
  const full = ctx.mode !== "card";

  const next = () => {
    if (!active) return;
    if (!submitCurrent()) setShake((n) => n + 1);
  };
  const autoAdvance = () => {
    clearTimeout(autoTimer.current);
    autoTimer.current = setTimeout(() => {
      // Only if this question is still the one on screen (no double advance).
      if (useSurveyV2Store.getState().currentBlock()?.id === block.id) {
        submitCurrent();
      }
    }, AUTO_ADVANCE_MS);
  };
  const pick = (value: SurveyAnswerValue) => {
    setDraft(block.id, value);
    autoAdvance();
  };

  // Re-trigger the shake when the store reports a validation error from the
  // keyboard path (Enter / arrow down) too.
  const lastError = useRef(error);
  useEffect(() => {
    if (error && error !== lastError.current) setShake((n) => n + 1);
    lastError.current = error;
  }, [error]);

  let control: ReactNode = null;
  let showButton = true;
  // A preselected answer on an auto-advancing question still needs a way on: show Next.
  const preselected =
    block.defaultValue !== undefined && draft !== undefined && JSON.stringify(draft) === JSON.stringify(block.defaultValue);
  switch (block.type) {
    case "nps":
    case "csat":
    case "rating":
    case "scale":
      control = (
        <ScaleControl
          block={block}
          value={draft}
          ctx={ctx}
          titleId={titleId}
          onPick={(v) => (isAutoAdvance(block) ? pick(v) : setDraft(block.id, v))}
        />
      );
      showButton = !isAutoAdvance(block) || preselected;
      break;
    case "single":
    case "multi": {
      control = (
        <ChoiceControl
          block={block}
          value={draft}
          ctx={ctx}
          titleId={titleId}
          order={order}
          onChange={(v) => setDraft(block.id, v)}
          onAutoAdvance={autoAdvance}
        />
      );
      const otherPicked = !!draft && typeof draft === "object" && !Array.isArray(draft);
      showButton = block.type === "multi" || otherPicked || preselected;
      break;
    }
    case "yesno":
      control = (
        <YesNoControl block={block} value={draft} ctx={ctx} titleId={titleId} onPick={pick} />
      );
      showButton = preselected;
      break;
    case "short":
    case "long":
    case "email":
    case "number":
    case "date":
      control = (
        <TextControl
          block={block}
          value={draft}
          ctx={ctx}
          titleId={titleId}
          errorId={errorId}
          invalid={!!error}
          onChange={(v) => setDraft(block.id, v)}
        />
      );
      break;
    case "upload":
      control = (
        <UploadControl
          block={block}
          value={draft}
          ctx={ctx}
          titleId={titleId}
          onChange={(v) => setDraft(block.id, v)}
        />
      );
      break;
    case "statement":
      control = null;
      break;
    case "consent":
      control = (
        <ConsentControl block={block} value={draft} ctx={ctx} titleId={titleId} onChange={(v) => setDraft(block.id, v)} />
      );
      break;
  }

  // A consent's text is its checkbox label (ConsentControl), not a heading.
  const title = block.type === "consent" ? "" : text(ctx, block.title);
  const description = text(ctx, block.description);
  const hint = selectionHint(ctx, block);

  return (
    <div className={`sv2-q sv2-q--${block.type}`} data-block={block.id}>
      {!full && <SenderRow ctx={ctx} />}
      {full && number > 0 && (
        <div className="sv2-qn" aria-hidden="true">
          {number}
          <SurveyIcon name="arrow-right" size={14} strokeWidth={2.4} className="sv2-flip-rtl" />
        </div>
      )}
      <h2 className="sv2-title" id={titleId} tabIndex={-1}>
        {full && number > 0 && <span className="sv2-sr">{ctx.t("svQuestion", { n: number })}: </span>}
        {title}
        {!block.required && block.type !== "statement" && block.type !== "consent" && (
          <span className="sv2-optional"> {ctx.t("svOptional")}</span>
        )}
      </h2>
      {description && <p className="sv2-desc">{description}</p>}
      {hint && <p className="sv2-hint-line">{hint}</p>}
      <Media media={block.media} />
      <div ref={shakeRef} className="sv2-ctl-wrap">
        {control}
      </div>
      {error && (
        <p className="sv2-error" id={errorId} role="alert">
          <SurveyIcon name="alert" size={14} />
          {errorText(ctx, block, error)}
        </p>
      )}
      <NextRow
        block={block}
        ctx={ctx}
        showButton={showButton}
        answered={hasDraftValue(draft)}
        onNext={next}
        onSkip={() => active && skipCurrent()}
      />
    </div>
  );
}

// ── Ending ────────────────────────────────────────────────────────────────

export function EndingBody({
  ending,
  ctx,
  onButton,
}: {
  ending: SurveyEnding;
  ctx: SurveyRenderContext;
  onButton: (url: string) => void;
}) {
  const title = text(ctx, ending.title);
  const description = text(ctx, ending.description);
  const buttonLabel = ending.button ? text(ctx, ending.button.label) : "";
  return (
    <div className="sv2-end" role="status">
      <div className="sv2-end-ok" aria-hidden="true">
        <SurveyIcon name="check" size={22} strokeWidth={2.4} />
      </div>
      <h2 className="sv2-title" tabIndex={-1} data-autofocus>
        {title}
      </h2>
      {description && <p className="sv2-desc">{description}</p>}
      {ending.button?.url && buttonLabel && (
        <button
          type="button"
          className="sv2-btn"
          onClick={() => onButton(ending.button!.url)}
        >
          {buttonLabel}
          <SurveyIcon name="arrow-up-right" size={16} strokeWidth={2.2} />
        </button>
      )}
    </div>
  );
}

// ── Welcome ───────────────────────────────────────────────────────────────

/** "Takes about N minutes": ~15 s per question (statements and questions skipped as known don't count), at least 1. */
export const welcomeMinutes = (def: SurveyDefinition, known: Record<string, string> = {}) =>
  Math.max(
    1,
    Math.round(def.blocks.filter((b) => b.type !== "statement" && known[b.id] === undefined).length * 0.25),
  );

/**
 * The welcome screen before the first question. Full screen / page: the
 * Typeform-style centred intro (image, large title, description, Start with
 * "press Enter ↵", time estimate). Card / sheet: the compact version (title,
 * description, Start).
 */
export function WelcomeBody({
  welcome,
  ctx,
  onStart,
}: {
  welcome: SurveyWelcome;
  ctx: SurveyRenderContext;
  onStart: () => void;
}) {
  const full = ctx.mode !== "card";
  const title = text(ctx, welcome.title);
  const description = text(ctx, welcome.description);
  // No label in the respondent's (or the default) language: the built-in Start
  // in their language, never another language's custom label.
  const label =
    fillTokens(
      pickLocalized(welcome.buttonLabel, ctx.lang, ctx.def.defaultLanguage, false),
      ctx.tokens,
    ).trim() || ctx.t("svStart");
  const known = useSurveyV2Store((s) => s.known);
  const minutes = welcomeMinutes(ctx.def, known);
  const showTime = welcome.showTimeToComplete !== false;
  const time = showTime && (
    <span className="sv2-welcome-time">
      <SurveyIcon name="clock" size={full ? 14 : 13} />
      {minutes === 1
        ? ctx.t("svTakesAboutOne")
        : ctx.t("svTakesAbout", { n: minutes })}
    </span>
  );
  return (
    <div className="sv2-welcome">
      {full && welcome.media?.url && (
        <div className="sv2-welcome-media">
          <img src={welcome.media.url} alt="" />
        </div>
      )}
      <h2 className="sv2-title" tabIndex={-1}>
        {title}
      </h2>
      {description && <p className="sv2-desc">{description}</p>}
      <div className="sv2-welcome-actions">
        <button type="button" className="sv2-btn" onClick={onStart}>
          {label}
          {full && (
            <SurveyIcon
              name="arrow-right"
              size={16}
              strokeWidth={2.4}
              className="sv2-flip-rtl"
            />
          )}
        </button>
        {full && ctx.hasKeyboard && (
          <span className="sv2-hint" aria-hidden="true">
            {ctx.t("svPressEnter")} <b>{ctx.t("svEnter")} ↵</b>
          </span>
        )}
        {/* Card: the estimate sits beside Start; full screen: its own line. */}
        {!full && time}
      </div>
      {full && time && <p className="sv2-welcome-time-line">{time}</p>}
    </div>
  );
}
